
import React from 'react';
import { AppUser, UserRole } from '../../../types';
import { XMarkIcon, CienCuadrasLogoImg } from '../../Icons';

interface VirtualCarnetModalProps {
  isOpen: boolean;
  onClose: () => void;
  appUser: AppUser;
}

const getRoleConfig = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPERADMIN: return { label: 'Super Admin', color: 'bg-red-600', text: 'text-white' };
      case UserRole.ADMIN: return { label: 'Administrador', color: 'bg-yellow-500', text: 'text-white' };
      case UserRole.BROKER_PREMIUM: return { label: 'Broker Premium', color: 'bg-indigo-600', text: 'text-white' };
      case UserRole.BROKER_INTERNO: return { label: 'Broker Interno', color: 'bg-sky-600', text: 'text-white' };
      case UserRole.COMERCIAL_INTERNO: return { label: 'Comercial Interno', color: 'bg-teal-600', text: 'text-white' };
      default: return { label: 'Colaborador', color: 'bg-sky-400', text: 'text-white' };
    }
};

export const VirtualCarnetModal: React.FC<VirtualCarnetModalProps> = ({ isOpen, onClose, appUser }) => {
    if (!isOpen) return null;

    const roleInfo = getRoleConfig(appUser.role);

    return (
        <div className="fixed inset-0 bg-gray-900/95 backdrop-blur-md z-[200] flex items-center justify-center p-4 overflow-hidden">
            <div className="relative animate-fade-in-up flex flex-col items-center">
                {/* Close Button UI - Fixed position above the card */}
                <button 
                    onClick={onClose} 
                    className="absolute -top-10 right-0 text-white hover:text-red-400 transition-colors flex items-center gap-2 font-black uppercase text-[11px] tracking-[0.2em] z-50"
                >
                    Cerrar <XMarkIcon className="w-6 h-6" />
                </button>

                {/* THE CARNET - VERTICAL DESIGN */}
                <div className="w-72 sm:w-80 h-[460px] sm:h-[480px] bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col border border-gray-100 relative group">
                    {/* Header: Brand Background */}
                    <div className="h-28 sm:h-32 bg-gradient-to-br from-[#00234F] to-[#005A9E] relative flex items-center justify-center p-6">
                        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.ciencuadras.com/sources/images/logo-cc-sprite.svg')] bg-repeat bg-[length:100px_50px]"></div>
                        <CienCuadrasLogoImg className="h-8 sm:h-10 brightness-0 invert relative z-10" />
                    </div>

                    {/* Content: Profile Section */}
                    <div className="flex-grow flex flex-col items-center pt-10 px-8 pb-8 text-center relative">
                        {/* Photo: Float on the line */}
                        <div className="absolute -top-12 sm:-top-14 left-1/2 -translate-x-1/2">
                            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 sm:border-8 border-white bg-white overflow-hidden shadow-xl">
                                <img 
                                    src={appUser.photoURL || 'https://www.gravatar.com/avatar/0000?d=mp'} 
                                    className="w-full h-full object-cover" 
                                    alt="Identidad" 
                                />
                            </div>
                        </div>

                        {/* Name & Role */}
                        <div className="mt-4 w-full">
                            <h2 className="text-lg sm:text-xl font-black text-gray-900 uppercase tracking-tighter leading-tight mb-2 truncate px-2">
                                {appUser.nombreCompleto || appUser.displayName}
                            </h2>
                            <div className={`inline-block px-4 py-1.5 rounded-full ${roleInfo.color} ${roleInfo.text} text-[9px] sm:text-[10px] font-black uppercase tracking-widest shadow-md mb-4 sm:mb-6`}>
                                {roleInfo.label}
                            </div>
                        </div>

                        {/* Identification Details */}
                        <div className="w-full space-y-3 sm:space-y-4 pt-4 border-t border-gray-50">
                            <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Tipo de Documento</p>
                                <p className="text-xs sm:text-sm font-bold text-gray-700">{appUser.tipoIdentificacion || 'Cédula de Ciudadanía'}</p>
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Nro. Identificación</p>
                                <p className="text-lg sm:text-xl font-black text-[#00234F] tracking-tighter">{appUser.numeroIdentificacion || 'N/A'}</p>
                            </div>
                        </div>

                        {/* Footer / QR (Simulated) */}
                        <div className="mt-auto pt-4 sm:pt-6 flex flex-col items-center">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-50 rounded-2xl border-2 border-gray-100 flex items-center justify-center p-2 opacity-80 group-hover:opacity-100 transition-opacity">
                                <div className="w-full h-full bg-[url('https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=CIENCUADRAS_VIRTUAL_ID')] bg-cover"></div>
                            </div>
                            <p className="text-[8px] font-black text-gray-300 uppercase tracking-[0.3em] mt-2 sm:mt-3">Ciencuadras Identity</p>
                        </div>
                    </div>

                    {/* Holographic effect line */}
                    <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-transparent via-sky-300/30 to-transparent group-hover:left-full transition-all duration-1000 ease-in-out pointer-events-none"></div>
                </div>

                <div className="mt-6 text-center px-4">
                    <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">Este carnet es un documento digital <br/> de uso exclusivo en la plataforma</p>
                </div>
            </div>
        </div>
    );
};
